import { Button } from "@/components/ui/button";
import ModalPopup from "@/components/ui/modalpopup";
import { toast } from "@/hooks/use-toast";
import { useFullScreen } from "@component/fullscreen/FullScreenWrapper";
import StatusBadge from "@component/statusbadgestailwind/StatusBadge";
import NewCustomTable from "@component/table/NewCustomTable";
import Tags from "@component/tags";
import { API_ROUTES, apiPost } from "@configure/apiConfig";
import { actionNameConfig, SocketIdType } from "@configure/menuAndSubmenuConfig";
import ColumnHeader from "@screens/RM/people/peoplelanding/servicecomponents/inputComponents/ColumnHeader";
import { exportFile } from "@utils/exportFile";
import { NoRecordsText } from "globalStyles";
import { Check, Edit2, Eye, MessageSquareText, X } from "lucide-react";
import moment from "moment";
import React, { forwardRef, memo, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import EllipsisTooltip from "@component/tooltip/EllipsisTooltip";
import {
    Popover,
    PopoverTrigger,
    PopoverContent,
} from "@/components/ui/popover";
import { updateAllocationStatus } from "@hooks/useUpdateAllocationStatus";
import { Textarea } from "@/components/ui/textarea";
import { PopoverPortal } from "@radix-ui/react-popover";
import { LoaderComponent } from "@auth/PrivateRoute";
import { getBandLabel } from "@utils/bandFormatter";


const exactNumberFilter = (row, columnId, filterValue) => {
    const rowValue = row.getValue(columnId);

    // Ensure numeric comparison (avoid string mismatch)
    const numRowValue = parseFloat(rowValue);
    const numFilterValues = Array.isArray(filterValue)
        ? filterValue.map((v) => parseFloat(v))
        : [parseFloat(filterValue)];

    return numFilterValues.includes(numRowValue);
};

const AllResourceTabTables = forwardRef(({
    currentTab,
    currentTabIndex,
    filteredPeople,
    profileData,
    onclickRoute,
    isAuthenticated,
    refreshData,
    notesLoader,
    columnVisibility={},
    setRefData=()=>null
}, ref) => {

    const [tableData, setTableData] = useState([]);
    const [chipData, setChipData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [pinnedColumns, setPinnedColumns] = React.useState({ right: ["mrt-row-actions"], left: ["employeeid", "fullname"] });
    const [editingRowValues, setEditingRowValues] = useState({});
    const [openDownloadModal, setOpenDownloadModal] = useState(false);
    const [tableExport, setTableExport] = useState(null);
    const scrollXRef = useRef();
    const tableRef = useRef();
    const pathname = useLocation().pathname;
    const isReportGenProfilePage = pathname.includes('/rm/reports');

    const { wrapperRef } = useFullScreen();

    useImperativeHandle(ref, () => ({
        getData: () => tableRef?.current?.getTable()?.getFilteredRowModel().rows ?? [],
        getTable: () => tableRef?.current?.getTable()
    })

    )

    

    useEffect(() => {
        if(tableRef?.current){
            setRefData(tableRef?.current?.getTable())
        }
    }, [tableRef?.current]);
    useEffect(() => {
        if (filteredPeople) {
            if (filteredPeople.length > 0) {
                // show filtered data
                setTableData(filteredPeople);
            } else {
                // no results → trigger empty state
                setTableData([]);
            }
        } else {
            // show full dataset
            setTableData(profileData || []);
        }
    }, [filteredPeople, profileData, currentTab]);

    // download the data into csv file
    const handleDownloadClick = () => {
        if (!tableExport) return;
        const filteredData = tableExport.getFilteredRowModel().rows.map((row) => row.original);
        const fileName = `rmpeople_${currentTab}`;
        try {
            exportFile("csv", filteredData, fileName);
            toast({ description: 'Successfully Exported', title: 'Success' });
        } catch (err) {
            toast({ description: 'Export failed', title: 'Error', variant: "destructive" });
        }
        setOpenDownloadModal(false);
        setTableExport(null);
    };


    const navigate = useNavigate();

    const onClickRow = (employeeID, status) => {
        navigate(`${onclickRoute}/${employeeID}`, {
            state: { employeeID, currentTabIndex },
        });
    };

    const handleSave = async ({ row, table }) => {
        if (isLoading) return;
        setIsLoading(true);
        table.setEditingRow(null);
        const projectId = row.original?.current_projects?.project_id;
        const employeeId = row.original.employeeid;

        // 🚫 If no project id → show toast & stop execution
        if (!projectId) {
            toast({
                description: "Employee is not assigned to a project",
                title: "Cannot Save Changes",
                variant: "destructive",
            });
            table.setEditingRow(null);
            return;
        }

        // ✅ Update probability_of_extention
        if (editingRowValues["probability_of_extention"] !== row.original.probability_of_extention) {
            const payload = {
                employeeId,
                probabilityOfExtention: Number(editingRowValues["probability_of_extention"]),
                type: "updatePropabilityOfextention",
                projectId,
            };
            await apiPost(API_ROUTES.UPDATE_RM_SKILS, payload);
        }

        // ✅ Update target properties
        const targetPropertiesPayload = {
            employeeId,
            targetAccount: editingRowValues["target_account"],
            commitStatus: editingRowValues["commit_status"],
            targetStartDate: editingRowValues["target_start_date"],
            benchNotes: editingRowValues["bench_notes"],
            type: "targetProperties",
        };

        try {
            const response = await apiPost(
                API_ROUTES.UPDATE_RM_SKILS,
                targetPropertiesPayload,
                null,
                {
                    "Socket-Action": actionNameConfig.RM.PEOPLE.EMPLOYEE_COMMIT_STATUS,
                    "Socket-Deal-Id": employeeId,
                    "Socket-Id-Type": SocketIdType.EmployeeId,
                    "Socket-Deal-Status": null,
                }
            );
            if (response.status === 200) {
                toast({
                    description: "Target Properties saved successfully",
                    title: "Success",
                });
                if (typeof refreshData === "function") {
                    await refreshData();
                }
            }
        } catch (error) {
            console.error("Error saving data:", error);
        } finally {
            setPinnedColumns({ right: ["mrt-row-actions"], left: ["employeeid", "fullname"] });
            table.setEditingRow(null);
            setIsLoading(false);
        }
    };


    const globalPropertiesColumns = useMemo(() => {
        if (currentTab === "global_forecasting" && tableData?.[0]) {
            return Object.keys(tableData?.[0]).map((columnId) => {
                if (moment(columnId, moment.ISO_8601, true).isValid()) {
                    return {
                        header: columnId,
                        accessorKey: columnId,
                        id: columnId,
                        maxSize: 100,
                        enableColumnFilter: false,
                        Cell: ({ cell }) => {
                            const value = cell.getValue();
                            return (value !== null && value !== undefined) ? value : "NA";
                        },
                    };
                }
                return null;
            }).filter(Boolean);
        }
        return [];
    }, [currentTab, tableData]);

    const handleOnDeleteForFilterTags = (_, removableElement) => {
        const updatedValue = chipData.filter(
            (item) => item.subtitle !== removableElement.subtitle
        );
        setChipData(updatedValue);
    };
    const columnVisibilityHeader = {
        geography: false,
        work_email: false,
        phone_number: false,
        employee_location: false,
        start_date: false,
        yoe_before_wavicle: false,
        wavicle_yoe: false,
        total_yoe: false,
        end_date: false,
        category: false,
        allocation_status: false,
        mentor_id: false,
    };
    const rowActions = ({ row, table, cell }) => {
        const employeeID = row.original.employeeid;
        const allocationValue = row.original.allocation_status;
        const status = allocationValue === "Y" ? "allocated" : "notallocated";

        return (
            <div className='flex gap-2'>
                <>
                    <Button
                        className="rounded-full !bg-transparent h-auto p-0 shadow-none"
                        id={`action-${employeeID}`}
                        onClick={() => onClickRow(employeeID, status)}
                        size="small"
                    >
                        <Eye className="!w-5 !h-5 !text-form-label" />
                    </Button>
                </>
            </div>
        );
    };

    const BenchNotesPopover = ({ value, employeeId, notesLoader }) => {
        const [isEditing, setIsEditing] = useState(false);
        const [inputValue, setInputValue] = useState(value || ''); // saved value  
        const [draftValue, setDraftValue] = useState(inputValue); // editable draft
        const [isPopoverOpen, setIsPopoverOpen] = useState(false);
        const [isNotesLoading, setIsNotesLoading] = useState(false);
        const { currentTab } = useParams();

        // Use draftValue when editing, otherwise inputValue
        const displayValue = isEditing ? draftValue : inputValue;
        const hasValue = displayValue.trim() !== '';
        const colorClass = hasValue ? "text-green-600" : "text-gray-400";

        const handleSave = async () => {
            const trimmedValue = draftValue.trim();

            // ✅ Validation: don't allow empty or "N/A"
            if (!trimmedValue || trimmedValue.toLowerCase() === "n/a") {
                toast({
                    title: "Validation Error",
                    description: "Please enter valid message before saving.",
                    variant: "destructive",
                });
                return;
            }
            setIsNotesLoading(true);
            const payload = {
                type: "update_bench_notes",
                benchNotes: draftValue,
                employeeId: employeeId
            };
            const header = {
                "Socket-Action": actionNameConfig.RM.PEOPLE.PROFILE.BENCH_NOTES_UPDATION,
            };

            try {
                console.log("Sending payload:", payload);

                const response = await updateAllocationStatus(header, payload); // API call (use await)

                if (response) {
                    refreshData(currentTab, "notes", employeeId); // Refresh data after successful update
                    toast({
                        title: "Success",
                        description: "Message updated successfully!",
                        containerRef: wrapperRef
                    });
                    setInputValue(draftValue);
                    setIsEditing(false);
                    // if (onEdit) onEdit(draftValue);
                } else {
                    toast({
                        title: "Error",
                        description: "Failed to update Message.",
                        variant: "destructive",
                        containerRef: wrapperRef
                    });
                }

            } catch (error) {
                console.error("API Error:", error);
                toast({
                    title: "Error",
                    description: "Something went wrong while saving.",
                    variant: "destructive",
                    containerRef: wrapperRef
                });
            } finally {
                setIsNotesLoading(false);
            }
        };


        const handleCancel = () => {
            setDraftValue(inputValue);
            setIsEditing(false);
        };

        return (
            <div className="absolute right-2">
                {isNotesLoading || notesLoader ? <LoaderComponent showText={false} height="5vh" /> : (
                    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                        <PopoverTrigger asChild>
                            <div
                                onMouseEnter={() => setIsPopoverOpen(true)}
                                onMouseLeave={() => {
                                    if (!isEditing) {          // <-- only close if not editing
                                        setIsPopoverOpen(false);
                                        setDraftValue(inputValue); // reset edits
                                    }
                                }}
                                className="p-1 rounded hover:bg-primary-foreground transition-colors cursor-pointer"
                                onClick={() => setIsPopoverOpen(true)}
                            >
                                <MessageSquareText className={`w-4 h-4 ${colorClass}`} />
                            </div>
                        </PopoverTrigger>
                        <PopoverPortal container={wrapperRef?.current}>
                            <PopoverContent
                                className="p-3 w-96 flex flex-col gap-2 z-[9999]"
                                onMouseEnter={() => setIsPopoverOpen(true)}
                                onMouseLeave={() => {
                                    if (!isEditing) setIsPopoverOpen(false); // don't close if editing
                                }}
                            >
                                {/* Label */}
                                <label className="text-[12px] font-bold text-foreground ">
                                    Message
                                </label>

                                {/* Text Field */}
                                <Textarea
                                    rows={8}
                                    placeholder="N/A"
                                    className={`text-[12px] border border-card-border w-full rounded px-1 py-0.5 resize-none 
                                      focus-visible:outline-none focus-visible:ring-0 focus-visible:border-card-border`}
                                    value={draftValue}
                                    readOnly={!isEditing || !isAuthenticated}
                                    onChange={(e) => setDraftValue(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                {!isReportGenProfilePage && <>
                                    {/* Buttons */}
                                    <div className="flex justify-end gap-2 mt-1">
                                        {isEditing ? (
                                            <>
                                                <button
                                                    className="text-green-600 hover:text-green-700"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSave();
                                                        setIsEditing(false);
                                                        setIsPopoverOpen(false);
                                                    }}
                                                >
                                                    <Check className="h-4 w-4" />
                                                </button>
                                                <button
                                                    className="text-red-400 hover:text-gray-600"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleCancel();
                                                        setIsEditing(false);
                                                        setIsPopoverOpen(false);
                                                    }}
                                                >
                                                    <X className="h-4 w-4" />
                                                </button>
                                            </>
                                        ) : (
                                            isAuthenticated && (
                                                <button
                                                    className="flex items-center gap-1"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setIsEditing(true);
                                                    }}
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                    {/* <span className="text-[11px]">Edit</span> */}
                                                </button>
                                            )
                                        )}
                                    </div>
                                </>}
                            </PopoverContent>
                        </PopoverPortal>
                    </Popover>
                )}
            </div>
        );
    };

    const forecastcolumns = useMemo(() => [
        {
            accessorKey: "employeeid",
            header: "Employee ID",
            enableEditing: false,
            enablePinning: true,
            enableHiding: false,
            minSize: 10,
            maxSize: 100,
            enableColumnFilter: false, // ✅ must be enabled
            filterFn: 'arrIncludesSome',
            Header: ({ column }) => <ColumnHeader containerRef={wrapperRef?.current} column={column || {}} />,
        },
                {
            accessorKey: "fullname",
            header: "Full Name",
            enableEditing: false,
            enablePinning: false,
            enableHiding: false,
            minSize: 10,
            maxSize: 400,
            enableColumnFilter: false,
            filterFn: 'arrIncludesSome',
            Header: ({ column }) => <ColumnHeader column={column || {}} containerRef={wrapperRef?.current} />,
            Cell: ({ cell, row }) => {
                const fullName = cell.getValue();
                const employeeId = row?.original?.employeeid;
                return (
                    <div className="flex items-center justify-between gap-2 w-full max-w-[300px]">
                        <EllipsisTooltip className="max-w-[130px]">
                            <span>
                                {fullName}
                            </span>
                        </EllipsisTooltip>
                        {isReportGenProfilePage ? (<BenchNotesPopover
                            value={row?.original?.bench_notes}
                            employeeId={row?.original?.employeeid}
                        />) : (
                            <BenchNotesPopover
                                value={row?.original?.bench_notes}
                                employeeId={row?.original?.employeeid}
                                notesLoader={notesLoader?.[employeeId] || false}
                            />)}
                    </div>

                )
            }
        },

        {
            accessorKey: "geography",
            header: "Geography",
            enablePinning: false,
            minSize: 10,
            maxSize: 100,
            Cell: ({ cell }) => {
                const value = cell.getValue();
                return value ? value : "NA";
            },
            enableColumnFilter: false, // ✅ must be enabled
            filterFn: 'arrIncludesSome',
            Header: ({ column }) => <ColumnHeader column={column || {}} containerRef={wrapperRef?.current} />,
        },
        {
            accessorFn: (row) => row?.band_,
            header: "Band",
            enablePinning: false,
            enableEditing: false,
            minSize: 10,
            maxSize: 100,
            Cell: ({ row }) => getBandLabel(row.original.band_),
            enableColumnFilter: false, // ✅ must be enabled
            filterFn:'arrIncludesSome',
            Header: ({ column }) => <ColumnHeader column={column || {}} containerRef={wrapperRef?.current} />,
        },
        {
            accessorKey: "utilization_percentage",
            header: "Allocation%",
            enablePinning: false,
            minSize: 10,
            maxSize: 150,
            Cell: ({ cell }) => {
                const value = cell.getValue();
                return value ? `${value}%` : "NA";
            },
            enableColumnFilter: false, // ✅ must be enabled
            filterFn: (row, columnId, filterValue) => {
                const cellValue = row.getValue(columnId); // single number

                if (!filterValue || filterValue.length === 0) return true;

                return filterValue.includes(cellValue);
            },
            Header: ({ column }) => <ColumnHeader column={column || {}} containerRef={wrapperRef?.current} />,
        },
        {
            accessorKey: "project_end_date",
            header: "Projected Roll Off Date",
            enablePinning: false,
            enableEditing: false,
            minSize: 10,
            maxSize: 150,
            muiTableHeadCellProps: {
                title: "Projected Roll Off Date",
            },
            Cell: ({ cell }) => {
                const value = cell.getValue();
                return value || 'N/A'
                // ? moment(value).format(useIntl["staffing.date.format"])
                // : "NA";
            },
            enableColumnFilter: false, // ✅ must be enabled
            filterFn: 'arrIncludesSome',
            Header: ({ column }) => <ColumnHeader column={column || {}} containerRef={wrapperRef?.current} />,
        },
        {
            accessorKey: "probability_of_extention",
            header: "Probability of Extension",
            enablePinning: false,
            minSize: 10,
            maxSize: 150,
            muiTableHeadCellProps: {
                title: "Probability of Extension",
            },
            Cell: ({ cell }) => {
                const value = cell.getValue();
                return value ? `${value}%` : "NA";
            },
            enableColumnFilter: false, // ✅ must be enabled
            // Custom filter function to handle single number filtering
            filterFn: (row, columnId, filterValue) => {
                const cellValue = row.getValue(columnId); // single number

                if (!filterValue || filterValue.length === 0) return true;

                return filterValue.includes(cellValue);
            },
            Header: ({ column }) => <ColumnHeader column={column || {}} containerRef={wrapperRef?.current} />,
        },
        ...globalPropertiesColumns,
    ], [globalPropertiesColumns])

    const columns = useMemo(
        () => [
            // Personal Information
            {
                accessorKey: "employeeid",
                header: "Employee ID",
                enableEditing: false,
                enablePinning: true,
                enableHiding: false,
                minSize: 10,
                maxSize: 100,
                enableColumnFilter: false, // ✅ must be enabled
                filterFn: 'arrIncludesSome',
                Header: ({ column }) => <ColumnHeader containerRef={wrapperRef?.current} column={column || {}} />,
            },
            {
                id: "fullname",
                accessorFn: (row) => row?.fullname?.trim() ?? 'N/A',
                header: "Full Name",
                enableEditing: false,
                enablePinning: true,
                enableHiding: false,
                minSize: 100,
                maxSize: 400,
                enableColumnFilter: false,
                filterFn: 'arrIncludesSome',
                Header: ({ column }) => <ColumnHeader containerRef={wrapperRef?.current} column={column || {}} />,
                Cell: ({ cell, row }) => {
                    const fullName = cell.getValue();
                    const employeeId = row?.original?.employeeid;
                    return (
                        <div className="flex items-center justify-between gap-2 w-full max-w-[300px]">
                            <EllipsisTooltip className="max-w-[130px]">
                                <span>
                                    {fullName}
                                </span>
                            </EllipsisTooltip>
                            {isReportGenProfilePage ? (<BenchNotesPopover
                                value={row?.original?.bench_notes}
                                employeeId={row?.original?.employeeid}
                            />) : (
                                <BenchNotesPopover
                                    value={row?.original?.bench_notes}
                                    employeeId={row?.original?.employeeid}
                                    notesLoader={notesLoader?.[employeeId] || false}
                                />)}
                        </div>
                    )
                }
            },
            {
                accessorFn: (row) => row?.work_email?.trim() ?? 'N/A',
                header: "Work Email",
                enableEditing: false,
                enablePinning: false,
                minSize: 400,
                maxSize: 800,
                enableColumnFilter: false, // ✅ must be enabled
                filterFn: 'arrIncludesSome',
                Header: ({ column }) => <ColumnHeader containerRef={wrapperRef?.current} column={column || {}} />,
            },
            {
                accessorFn: (row) => row?.employee_location?.trim() ?? 'N/A',
                header: "Employee Location",
                enableEditing: false,
                enablePinning: false,
                minSize: 10,
                maxSize: 150,
                enableColumnFilter: false, // ✅ must be enabled
                filterFn: 'arrIncludesSome',
                Header: ({ column }) => <ColumnHeader containerRef={wrapperRef?.current} column={column || {}} />,
            },

            // Employment Details
            {
                accessorFn: (row) => row?.departmentname?.trim() ?? "N/A",
                header: "Department",
                enableEditing: false,
                enablePinning: false,
                minSize: 10,
                maxSize: 150,
                enableColumnFilter: false, // ✅ must be enabled
                filterFn: 'arrIncludesSome',
                Header: ({ column }) => <ColumnHeader containerRef={wrapperRef?.current} column={column || {}} />,
            },
            {
                accessorFn: (row) => row.rolename?.split("-")[0]?.trim() ?? "N/A",
                header: "Role",
                enableEditing: false,
                enablePinning: false,
                minSize: 10,
                maxSize: 170,
                enableColumnFilter: false, // ✅ must be enabled
                filterFn: 'arrIncludesSome',
                Header: ({ column }) => <ColumnHeader containerRef={wrapperRef?.current} column={column || {}} />,
            },
            {
                accessorFn: (row) => {
                    const name = row?.managername?.trim();
                    return name && name !== "" ? name : "N/A"; // ✅ covers empty string & null/undefined
                },
                header: "Manager/Mentor",
                enableEditing: false,
                enablePinning: false,
                minSize: 10,
                maxSize: 150,
                enableColumnFilter: false, // ✅ must be enabled
                filterFn: 'arrIncludesSome',
                Header: ({ column }) => <ColumnHeader containerRef={wrapperRef?.current} column={column || {}} />,
            },
            {
                accessorFn: (row) => row?.employmenttype?.trim() ?? 'N/A',
                header: "Employment Type",
                enableEditing: false,
                enablePinning: false,
                minSize: 10,
                maxSize: 140,
                muiTableHeadCellProps: {
                    title: "Employee Type",
                },
                Cell: ({ cell }) => {
                    console.log(cell.getValue());

                    return <StatusBadge status={cell.getValue() || 'N/A'} variant={"contain"} />;
                },
                enableColumnFilter: false, // ✅ must be enabled
                filterFn: 'arrIncludesSome',
                Header: ({ column }) => <ColumnHeader containerRef={wrapperRef?.current} column={column || {}} />,
            },
            {
                accessorKey: "status_",
                header: "Status",
                enableEditing: false,
                enablePinning: false,
                minSize: 10,
                maxSize: 100,
                Cell: ({ cell }) => {
                    const value = cell?.getValue()?.trim();
                    return (
                        <StatusBadge status={value === "TRUE" ? "active" : "in-active"} />
                    );
                },
                enableColumnFilter: false, // ✅ must be enabled
                filterFn: 'arrIncludesSome',
                Header: ({ column }) => <ColumnHeader containerRef={wrapperRef?.current} column={column || {}} />,
            },

            // Dates and Experience
            {
                accessorKey: "start_date",
                header: "Start Date",
                enableEditing: false,
                enablePinning: false,
                minSize: 10,
                maxSize: 150,
                Cell: ({ cell }) => {
                    const value = cell.getValue();
                    return value || "N/A"
                    // ? moment(value).format(useIntl["staffing.date.format"])
                    // : "NA";
                },
                enableColumnFilter: false, // ✅ must be enabled
                filterFn: 'arrIncludesSome',
                Header: ({ column }) => <ColumnHeader containerRef={wrapperRef?.current} column={column || {}} />,
            },
            {
                accessorKey: "end_date",
                header: "End Date",
                enableEditing: false,
                enablePinning: false,
                minSize: 10,
                maxSize: 150,
                Cell: ({ cell }) => {
                    const value = cell.getValue();
                    return value || "N/A"
                    // ? moment(value).format(useIntl["staffing.date.format"])
                    // : "NA";
                },
                enableColumnFilter: false, // ✅ must be enabled
                filterFn: 'arrIncludesSome',
                Header: ({ column }) => <ColumnHeader containerRef={wrapperRef?.current} column={column || {}} />,
            },
            {
                accessorKey: "total_yoe",
                header: "Total YOE",
                enableEditing: false,
                enablePinning: false,
                minSize: 10,
                maxSize: 100,
                Cell: ({ cell }) => {
                    const value = cell.getValue();
                    return value; //here value is in months convert to years
                },
                enableColumnFilter: false, // ✅ must be enabled
                filterFn: exactNumberFilter,
                Header: ({ column }) => <ColumnHeader containerRef={wrapperRef?.current} column={column || {}} />,
            },
            {
                accessorKey: "wavicle_yoe",
                header: "Wavicle YOE",
                enableEditing: false,
                enablePinning: false,
                minSize: 10,
                maxSize: 100,
                Cell: ({ cell }) => {
                    const value = cell.getValue();
                    return value; //here value is in months convert to years
                },
                enableColumnFilter: false, // ✅ must be enabled
                filterFn: exactNumberFilter,
                Header: ({ column }) => <ColumnHeader containerRef={wrapperRef?.current} column={column || {}} />,
            },
            {
                accessorKey: "yoe_before_wavicle",
                header: "YOE Before Wavicle",
                enableEditing: false,
                enablePinning: false,
                minSize: 10,
                maxSize: 100,
                Cell: ({ cell }) => {
                    const value = cell.getValue();
                    return value; //here value is in months convert to years
                },
                enableColumnFilter: false, // ✅ must be enabled
                filterFn: exactNumberFilter,
                Header: ({ column }) => <ColumnHeader containerRef={wrapperRef?.current} column={column || {}} />,
            },
            {
                accessorKey: "project_end_date",
                header: "Projected Roll Off Date",
                enablePinning: false,
                enableEditing: false,
                minSize: 10,
                maxSize: 150,
                muiTableHeadCellProps: {
                    title: "Projected Roll Off Date",
                },
                Cell: ({ cell }) => {
                    const value = cell.getValue();
                    return value || 'N/A'
                    // ? moment(value).format(useIntl["staffing.date.format"])
                    // : "NA";
                },
                enableColumnFilter: false, // ✅ must be enabled
                filterFn: 'arrIncludesSome',
                Header: ({ column }) => <ColumnHeader containerRef={wrapperRef?.current} column={column || {}} />,
            },
            // Probability of Extension
            ...(currentTab === "rm_sheet"
                ? [
                    {
                        accessorKey: "probability_of_extention",
                        header: "Probability of Extension",
                        enablePinning: false,
                        minSize: 10,
                        maxSize: 150,
                        muiTableHeadCellProps: {
                            title: "Probability of Extension",
                        },
                        enableColumnFilter: false, // ✅ must be enabled
                        filterFn: (row, columnId, filterValue) => {
                            const cellValue = row.getValue(columnId); // single number

                            if (!filterValue || filterValue.length === 0) return true;

                            return filterValue.includes(cellValue);
                        },
                        Header: ({ column }) => <ColumnHeader containerRef={wrapperRef?.current} column={column || {}} />,
                        Cell: ({ cell }) => {
                            const value = cell.getValue();
                            return value ? `${value}%` : "NA";
                        },
                    },
                ]
                 : []),

            // Additional Details
            {
                accessorFn: (row) => row?.band_,
                header: "Band",
                enablePinning: false,
                enableEditing: false,
                minSize: 10,
                maxSize: 100,
                Cell: ({ row }) => getBandLabel(row.original.band_),
                enableColumnFilter: false, // ✅ must be enabled
                filterFn: 'arrIncludesSome',
                Header: ({ column }) => <ColumnHeader containerRef={wrapperRef?.current} column={column || {}} />,
            },
            {
                accessorKey: "allocation_status",
                header: "Allocation Status",
                enablePinning: false,
                enableEditing: false,
                minSize: 10,
                maxSize: 170,
                enableColumnFilter: true, // ✅ must be enabled
                filterFn: 'arrIncludesSome',
                Header: ({ column }) => <ColumnHeader containerRef={wrapperRef?.current} column={column || {}} />,
                Cell: ({ cell }) => {
                    const value = cell?.getValue()?.trim();

                    return (
                        <StatusBadge
                            status={value === "Y" ? "allocated" : "not allocated"}
                        />
                    );
                },
            },
            {
                accessorKey: "utilization_percentage",
                header: "Allocation%",
                enablePinning: false,
                minSize: 10,
                maxSize: 150,
                Cell: ({ cell }) => {
                    const value = cell.getValue();
                    return value ? `${value}%` : "NA";
                },
                enableColumnFilter: false, // ✅ must be enabled
                filterFn: (row, columnId, filterValue) => {
                    const cellValue = row.getValue(columnId); // single number

                    if (!filterValue || filterValue.length === 0) return true;

                    return filterValue.includes(cellValue);
                },
                Header: ({ column }) => <ColumnHeader column={column || {}} containerRef={wrapperRef?.current} />,
            },
            {
                accessorFn: (row) => row?.category?.trim() ?? 'N/A',
                header: "Category",
                enablePinning: false,
                enableEditing: false,
                minSize: 10,
                maxSize: 100,
                enableColumnFilter: false, // ✅ must be enabled
                filterFn: 'arrIncludesSome',
                Header: ({ column }) => <ColumnHeader containerRef={wrapperRef?.current} column={column || {}} />,
            },
        ],
        [currentTab, editingRowValues]
    );
    
    const getRowId = (row) => row.employeeid;
    const muiTableBodyCellEditTextFieldProps = ({ cell }) => ({
        placeholder: `Edit ${cell.column.columnDef.header}`,
    });
    const muiTableBodyRowProps = ({ row }) => ({
        'data-row-id': row.id,
    });
    return (
        <>
            <Tags
                containerStyle={{ paddingBottom: "10px" }}
                deleteItem
                payload={chipData}
                onDelete={handleOnDeleteForFilterTags}
            />
            <NewCustomTable
                scrollX={scrollXRef}
                ref={tableRef}
                tableOptions={{
                    coreProps: {
                        getRowId,
                        columns: currentTab === 'global_forecasting' ? forecastcolumns : columns,
                        data: tableData || [],
                    },
                    enableProps: {
                        enableExpandAll: false,
                        enablePagination: true,
                        enableTopToolbar: false,
                        enableBottomToolbar: true,
                        createDisplayMode: "row",
                        enableColumnActions: false,
                        editDisplayMode: "row",
                        enableColumnFilters: false,
                        enableHiding: true,
                        positionCreatingRow: "bottom",
                        enableRowActions: true,
                        enableEditing: true,
                        columnPinning: pinnedColumns,
                        enableFacetedValues: true,
                        enableColumnPinning: true,
                        enableSorting: false,
                        columnFilterDisplayMode: "popover",
                        localization: {
                            noRecordsToDisplay: (
                                <NoRecordsText>No Records to Display</NoRecordsText>
                            ),
                        },
                    },
                    renderProps: {
                        renderRowActions: rowActions,
                        onEditingRowSave: handleSave,
                        state: {
                            columnPinning: pinnedColumns,
                            isLoading: isLoading,
                            columnVisibility:columnVisibility
                        },
                        displayColumnDefOptions: {
                            "mrt-row-actions": {
                                header: "View",
                                minSize: 80,
                                muiTableBodyCellProps: {
                                    sx: {
                                        "& div": {
                                            justifyContent: {
                                                xs: "space-between",
                                                sm: "flex-start",
                                            },
                                            width: "100%",
                                            gap: {
                                                xs: 0,
                                                sm: "0.4rem",
                                                md: "0.5rem",
                                                lg: "0.6rem",
                                                xl: "0.7rem",
                                            },
                                        },
                                        "& button": {
                                            padding: 0,
                                        },
                                    },
                                },
                            },
                        },
                    },

                    muiProps: {
                        muiTableBodyCellEditTextFieldProps: muiTableBodyCellEditTextFieldProps,
                        muiTableBodyRowProps: muiTableBodyRowProps,
                        muiPaginationProps: {
                            SelectProps: {
                                MenuProps: {
                                    container: wrapperRef?.current, // 👈 your ref here
                                },
                            },
                        },
                        initialState: {
                            columnPinning: {
                                right: ["mrt-row-actions"],
                                left: ["employeeid", "fullname"]
                            },
                        },
                    },
                    initialState: {
                        columnPinning: {
                            right: ["mrt-row-actions"],
                            left: ["employeeid", "fullname"]
                        },
                    },
                }}
            />
            {/*  Confirmation for Download the Excel Popup */}
            <ModalPopup
                open={openDownloadModal}
                handlePopup={() => setOpenDownloadModal(false)}
                cancel={() => setOpenDownloadModal(false)}
                accept={handleDownloadClick}
                header="Are you sure you want to export?"
                cancelAction="Cancel"
                confirmAction="OK"
            />

        </>
    )
})

export default memo(AllResourceTabTables);


